import * as React from 'react'
import produce from 'immer'
import { Form, Input, Spin, Button, Tooltip, Tabs } from 'antd'
import { get, set, uniq, cloneDeep, orderBy, isEmpty } from 'lodash'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import { createLabelFactory, createFieldFactory, createHandleSubmit, isReadOnly } from 'helpers/formViews'
import questionTypes from 'options/formTemplates/questionTypes'
import fontNames from 'options/fontNames'
import { createQuestion, allowRequired } from 'helpers/formTemplates'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import InputNumber from 'elements/InputNumber'
import Select, { Option, OptGroup, getOptionProps } from 'elements/Select'
import Checkbox from 'elements/Checkbox'
import Settings from './Settings'
import ColorPicker from 'elements/ColorPicker'
import { Row, Col } from 'elements/Grid'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  transformSaveItemParams = ({ item, values }) => ({
    required: allowRequired(item) ? values.required : false,
    defaultBoolean: Boolean(values.defaultBoolean),
    formQuestionOptions: get(values, 'formQuestionOptions', '')
      .split(/[\r\n]/)
      .filter((each) => !isEmpty(each))
      .filter((v, i, a) => a.indexOf(v) === i) // uniq
      .map((name, index) => ({
        id: this.state.formQuestionOptions.find((one) => one.name === name)?.id,
        name,
        value: name,
        position: index + 1,
      })),
  })

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.id].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem(createQuestion({ sectionId: this.props.parentRecord?.id }))
            : resourceNotFound(this),
        this.props.getSettings({ type: 'field' }),
      ])

      const item = cloneDeep(responses[0].value.data)

      const formQuestionOptions = item?.formQuestionOptions ?? []

      item.formQuestionOptions = orderBy(formQuestionOptions, ['position'])
        .map((each) => each.name)
        .join('\n')

      item.defaultBoolean = item.defaultBoolean ? 1 : 0

      if (!item.overrideSectionColor) {
        const { overrideFormColor } = this.props.parentRecord ?? {}

        item.foregroundColor =
          item.questionType === 'Hyperlink'
            ? '#0080FF'
            : overrideFormColor
              ? this.props.parentRecord?.contentForegroundColor
              : this.props.rootRecord?.formForegroundColor
        item.backgroundColor = overrideFormColor
          ? this.props.parentRecord?.contentBackgroundColor
          : this.props.rootRecord?.formBackgroundColor
      }

      this.setState({
        item,
        fieldSettings: responses[1].value.data.fieldSettings,
        formQuestionOptions,
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      }),
      () => {
        if (name === 'formQuestionOptions') {
          const options = value.split(/[\r\n]/).filter((each) => !isEmpty(each))

          if (!options.includes(this.state.item.defaultText)) {
            this.setItemValue('defaultText', '')
          }
        }
      }
    )

  render() {
    const { rootRecord } = this.props
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)

    const formPage = rootRecord.formPages.find((any) =>
      any.formPageSections.some((one) => one.id === item.sectionId)
    )

    const formPageSection = formPage.formPageSections.find((one) => one.id === item.sectionId)

    const allowInput = !['Hyperlink', 'Paragraph'].includes(item.questionType)

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Form.Item label={createLabel('questionType')}>
            {createFieldDecorator('questionType')(
              <Select
                onChange={(value) => this.setItemValue('questionType', value)}
                allowClear={false}
                readOnly={readOnly}
              >
                {Object.entries(questionTypes).map(([key, value]) => (
                  <Option key={key} value={key}>
                    {t(value)}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item label={createLabel('sectionId')}>
            {createFieldDecorator('sectionId')(
              <Select
                onChange={(value) => this.setItemValue('sectionId', value)}
                allowClear={false}
                readOnly={readOnly}
              >
                {rootRecord.formPages.map((page) => (
                  <OptGroup key={page.id} label={page.name}>
                    {page.formPageSections.map((each) => (
                      <Option key={each.id} value={each.id}>
                        <span {...getOptionProps(each)}>{each.name}</span>
                      </Option>
                    ))}
                  </OptGroup>
                ))}
              </Select>
            )}
          </Form.Item>
          <Tabs>
            <Tabs.TabPane key="field" tab={t('field')} forceRender>
              <Form.Item label={t(allowInput ? 'label' : 'text')}>
                {createFieldDecorator('text')(<Input.TextArea rows={5} readOnly={readOnly} />)}
              </Form.Item>
              <Row>
                <Col xs={12}>
                  <Form.Item label={createLabel('line')}>
                    {createFieldDecorator('line')(
                      <InputNumber
                        onChange={(value) => this.setItemValue('line', value)}
                        min={0}
                        max={uniq(formPageSection.formQuestions.map((each) => each.line)).length + 1}
                        readOnly={readOnly}
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item label={createLabel('position')}>
                    {createFieldDecorator('position')(
                      <InputNumber
                        min={1}
                        max={
                          formPageSection.formQuestions.filter((each) => each.line === item.line).length + 1
                        }
                        readOnly={readOnly}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
              {item.questionType === 'Hyperlink' && (
                <Form.Item label={createLabel('questionOption')}>
                  {createFieldDecorator('questionOption')(<Input type="url" readOnly={readOnly} />)}
                </Form.Item>
              )}
              {item.questionType === 'NumberInput' && (
                <>
                  <Row>
                    <Col xs={12}>
                      <Form.Item label={createLabel('minValue')}>
                        {createFieldDecorator('minValue')(<InputNumber readOnly={readOnly} allowClear />)}
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <Form.Item label={createLabel('maxValue')}>
                        {createFieldDecorator('maxValue')(<InputNumber readOnly={readOnly} allowClear />)}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <Form.Item label={createLabel('decimalPlaces')}>
                        {createFieldDecorator('decimalPlaces')(<InputNumber readOnly={readOnly} min={0} />)}
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <Form.Item label={createLabel('defaultNumber')}>
                        {createFieldDecorator('defaultNumber')(
                          <InputNumber readOnly={readOnly} allowClear />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
              {['Textbox', 'Textarea', 'Dropdown', 'Date', 'Time', 'DateTime'].includes(
                item.questionType
              ) && (
                <Form.Item label={createLabel('placeholderText')}>
                  {createFieldDecorator('placeholderText')(<Input readOnly={readOnly} />)}
                </Form.Item>
              )}
              {['Textbox', 'Textarea'].includes(item.questionType) && (
                <Form.Item label={createLabel('defaultText')}>
                  {createFieldDecorator('defaultText')(<Input readOnly={readOnly} />)}
                </Form.Item>
              )}
              {['Dropdown', 'SingleSelection'].includes(item.questionType) && (
                <Form.Item label={createLabel('defaultText')}>
                  {createFieldDecorator('defaultText')(
                    <Select onChange={(value) => this.setItemValue('defaultText', value)} readOnly={readOnly}>
                      {uniq(item.formQuestionOptions.split(/[\r\n]/).filter((each) => !isEmpty(each))).map(
                        (each) => (
                          <Option key={each} value={each}>
                            {each}
                          </Option>
                        )
                      )}
                    </Select>
                  )}
                </Form.Item>
              )}
              {item.questionType === 'Checkbox' && (
                <Form.Item label={createLabel('defaultBoolean')}>
                  {createFieldDecorator('defaultBoolean')(
                    <Select readOnly={readOnly} allowClear={false}>
                      <Option value={0}>{t('unchecked')}</Option>
                      <Option value={1}>{t('checked')}</Option>
                    </Select>
                  )}
                </Form.Item>
              )}
              {!['MultipleSelection', 'Hyperlink', 'Paragraph'].includes(item.questionType) && (
                <Form.Item>
                  {createFieldDecorator('required', { valuePropName: 'checked' })(
                    <Checkbox readOnly={readOnly}>{createLabel('required')}</Checkbox>
                  )}
                </Form.Item>
              )}
            </Tabs.TabPane>
            <Tabs.TabPane key="appearance" tab={t('appearance')} forceRender>
              <Settings
                item={item}
                onChange={(value) => this.setState({ item: value })}
                rootRecord={rootRecord}
                readOnly={readOnly}
              />
              {item.questionType === 'Textarea' && (
                <Row>
                  <Col xs={8}>
                    <Form.Item label={createLabel('height')}>
                      {createFieldDecorator('height')(<InputNumber min={1} readOnly={readOnly} />)}
                    </Form.Item>
                  </Col>
                </Row>
              )}
              {allowInput && (
                <Form.Item>
                  {createFieldDecorator('allowAttachment', { valuePropName: 'checked' })(
                    <Checkbox readOnly={readOnly}>{createLabel('allowAttachment')}</Checkbox>
                  )}
                </Form.Item>
              )}
              {!['SingleSelection', 'MultipleSelection', 'Hyperlink', 'Paragraph', 'Checkbox'].includes(
                item.questionType
              ) && (
                <Form.Item>
                  {createFieldDecorator('allowHighlight', { valuePropName: 'checked' })(
                    <Checkbox readOnly={readOnly}>{createLabel('allowHighlight')}</Checkbox>
                  )}
                </Form.Item>
              )}
              {!allowInput && (
                <>
                  <Row>
                    <Col xs={12}>
                      <Form.Item label={createLabel('fontName')}>
                        {createFieldDecorator('fontName')(
                          <Select placeholder={t('default')} allowClear={false} readOnly={readOnly}>
                            <Option key="" value="">
                              {t('default')}
                            </Option>
                            {fontNames.map((each) => (
                              <Option key={each} value={each}>
                                <span style={{ fontFamily: each }}>{each}</span>
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <Form.Item label={createLabel('fontSize')}>
                        {createFieldDecorator('fontSize')(<InputNumber min={8} readOnly={readOnly} />)}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <Form.Item>
                        {createFieldDecorator('bold', { valuePropName: 'checked' })(
                          <Checkbox readOnly={readOnly}>{createLabel('bold')}</Checkbox>
                        )}
                      </Form.Item>
                    </Col>
                    <Col xs={12}>
                      <Form.Item>
                        {createFieldDecorator('underline', { valuePropName: 'checked' })(
                          <Checkbox readOnly={readOnly}>{createLabel('underline')}</Checkbox>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item>
                    {createFieldDecorator('italic', { valuePropName: 'checked' })(
                      <Checkbox readOnly={readOnly}>{createLabel('italic')}</Checkbox>
                    )}
                  </Form.Item>
                  <Form.Item>
                    {createFieldDecorator('overrideSectionColor', { valuePropName: 'checked' })(
                      <Checkbox
                        onChange={(e) => this.setItemValue('overrideSectionColor', e.target.checked)}
                        readOnly={readOnly}
                      >
                        {item.questionType === 'Hyperlink'
                          ? t('overrideHyperlinkColor')
                          : createLabel('overrideSectionColor')}
                      </Checkbox>
                    )}
                  </Form.Item>
                  <Form.Item
                    label={
                      item.questionType === 'Hyperlink' ? t('hyperlinkColor') : createLabel('backgroundColor')
                    }
                  >
                    <Row type="flex" gutter={6}>
                      <Col>
                        <ColorPicker
                          value={item.backgroundColor}
                          onChange={(value) => this.setItemValue('backgroundColor', value)}
                          readOnly={readOnly}
                          disabled={!item.overrideSectionColor}
                        />
                      </Col>
                      <Col>
                        <ColorPicker
                          value={item.foregroundColor}
                          onChange={(value) => this.setItemValue('foregroundColor', value)}
                          readOnly={readOnly}
                          disabled={!item.overrideSectionColor}
                          foreground
                        />
                      </Col>
                    </Row>
                  </Form.Item>
                </>
              )}
            </Tabs.TabPane>
            {['Dropdown', 'SingleSelection', 'MultipleSelection'].includes(item.questionType) && (
              <Tabs.TabPane key="options" tab={t('options')} forceRender>
                <Form.Item label={createLabel('formQuestionOptions')}>
                  {createFieldDecorator('formQuestionOptions')(
                    <Input.TextArea
                      onChange={(e) => this.setItemValue('formQuestionOptions', e.target.value)}
                      rows={10}
                      readOnly={readOnly}
                    />
                  )}
                </Form.Item>
              </Tabs.TabPane>
            )}
          </Tabs>
        </div>
        <div className="form-buttons-container">
          <Button onClick={this.props.onCancel}>{t(readOnly ? 'close' : 'cancel')}</Button>{' '}
          {!readOnly && (
            <Tooltip title={t('childRecordSaveInfo')} placement="topLeft">
              <Button type="primary" htmlType="submit" loading={this.state.saveAndCloseButtonLoading}>
                {!this.props.linkTargetRecord ? t('add') : t('update')}
              </Button>
            </Tooltip>
          )}
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
